import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./UserDetailsPage.scss";
import { addCaseAssignmentToUser, deleteCaseAssignmentToUser, getCaseAssignmentToUserByUserID, getUserData } from "../../services/api/AuthApi";
import { useAuth } from "../../context/AuthContext";
import { FiUser, FiMail, FiPhone, FiCheckCircle, FiXCircle, FiShield, FiUserPlus, FiBriefcase, FiMapPin, FiClock, FiEdit, FiPlus, FiTrash2, FiSave, FiFileText } from "react-icons/fi";
import { BiEdit, BiTrash } from "react-icons/bi";
import { getBranchByBranchID } from "../../services/api/BranchApi";
// import { getCaseAssignmentToUserByUserID, addCaseAssignmentToUser, deleteCaseAssignmentToUser } from "../../services/api/CaseAssignmentApi";
import { getHighCasesByCourtRef, getDistrictCasesByCourtRef, getBothCourtCasesDetailsByCourtRef, getOtherCourtCasesAddedManuallyByCourtRef, getSupremeCourtCasesByCourtRef } from "../../services/api/CasesApi";
//import Toast from "../../components/Toast/Toast";
import { useToast } from "../../context/ToastContext";
import { useLoading } from "../../context/LoadingContext";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import { formatPartyNames,formatText } from "../../components/TruncateParties";
import { deleteAddedUser } from "../../services/api/AddUsersApi";


const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate()
  const { role: currentUserRole } = useAuth();
  const [createdByName, setCreatedByName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Toast and Modal states
  const { showToast } = useToast();
  const { showLoading, hideLoading } = useLoading();
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, assignmentId: null });
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);

  const [assignmentDetails, setAssignmentDetails] = useState({});
  // Case assignment states
  const [caseAssignments, setCaseAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCourtRef, setNewCourtRef] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Case dropdown states
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourtTab, setSelectedCourtTab] = useState('high');
  const [highCourtCases, setHighCourtCases] = useState([]);
  const [districtCourtCases, setDistrictCourtCases] = useState([]);
  const [otherCourtCases, setOtherCourtCases] = useState([]);
  const [supremeCourtCases, setSupremeCourtCases] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);

  const dropdownRef = useRef();
  const inputRef = useRef(null);

  // Helper function to check if current user can perform admin actions
  const canPerformAdminActions = () => {
    return currentUserRole === 'admin' || currentUserRole === 'superAdmin';
  };

  // Helper function to check if current user can access this user's details
  const canAccessUserDetails = () => {
    // Admin and superAdmin can access all users
    if (currentUserRole === 'admin' || currentUserRole === 'superAdmin') return true;
    // Non-admin can only access non-admin and non-superAdmin users
    return userData?.role !== 'admin' && userData?.role !== 'superAdmin';
  };

  // Fetch user data
  useEffect(() => {
    if (id) {
      setLoading(true);
      showLoading("Loading user details...");
      getUserData(id)
        .then(async (res) => {
          const user = res.data;
          setUserData(user);

          // Fetch Created By name
          if (user.createdBy) {
            try {
              const creatorRes = await getUserData(user.createdBy);
              setCreatedByName(creatorRes.data?.name || user.createdBy);
            } catch (e) {
              console.warn("Could not fetch creator's name");
              setCreatedByName(user.createdBy);
            }
          }

          // Fetch Branch Name
          if (user.branchIds?.length) {
            try {
              const branchRes = await getBranchByBranchID(user.branchIds[0]);
              setBranchName(branchRes.data?.result?.branchData?.branchName || user.branchIds[0]);
            } catch (e) {
              console.warn("Could not fetch branch name");
              setBranchName(user.branchIds[0]);
            }
          }

          setLoading(false);
          hideLoading();
        })
        .catch(() => {
          setLoading(false);
          hideLoading();
        });
    }
  }, [id, currentUserRole, navigate]);  

  // Fetch case assignments
  useEffect(() => {
    if (id) {
      fetchCaseAssignments();
    }
  }, [id]);

  // Fetch cases data for dropdown
  useEffect(() => {
    fetchCasesData();
  }, []);

  const fetchCaseAssignments = async () => {
    setLoadingAssignments(true);
    try {
      const response = await getCaseAssignmentToUserByUserID(id);
      const assignments = response.data?.caseAssignment || [];
      setCaseAssignments(assignments);

      // Fetch details for each assignment
      assignments.forEach(assignment => {
        if (assignment.courtRef) {
          fetchAssignmentDetails(assignment.courtRef);
        }
      });
    } catch (error) {
      console.error('Error fetching case assignments:', error);
      setCaseAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const fetchCasesData = async () => {
    setIsLoadingCases(true);
    try {
      // Handle each API call individually with error handling
      const fetchHighCourt = async () => {
        try {
          const response = await getHighCasesByCourtRef();
          return response.data || [];
        } catch (error) {
          console.error('Error fetching high court cases:', error);
          return [];
        }
      };

      const fetchDistrictCourt = async () => {
        try {
          const response = await getDistrictCasesByCourtRef();
          return response.data || [];
        } catch (error) {
          console.error('Error fetching district court cases:', error);
          return [];
        }
      };

      const fetchOtherCourt = async () => {
        try {
          const response = await getOtherCourtCasesAddedManuallyByCourtRef();
          return response.data || [];
        } catch (error) {
          console.error('Error fetching other court cases:', error);
          return [];
        }
      };

      const fetchSupremeCourt = async () => {
        try {
          const response = await getSupremeCourtCasesByCourtRef();
          return response.data || [];
        } catch (error) {
          console.error('Error fetching supreme court cases:', error);
          return [];
        }
      };

      // Execute all API calls concurrently but handle failures individually
      const [highCourtData, districtCourtData, otherCourtData, supremeCourtData] = await Promise.all([
        fetchHighCourt(),
        fetchDistrictCourt(),
        fetchOtherCourt(),
        fetchSupremeCourt()
      ]);

      setHighCourtCases(highCourtData);
      setDistrictCourtCases(districtCourtData);
      setOtherCourtCases(otherCourtData);
      setSupremeCourtCases(supremeCourtData);

    } catch (err) {
      console.error('Error in fetchCasesData:', err);
      // Set empty arrays as fallback
      setHighCourtCases([]);
      setDistrictCourtCases([]);
      setOtherCourtCases([]);
      setSupremeCourtCases([]);
    } finally {
      setIsLoadingCases(false);
    }
  };
  const fetchAssignmentDetails = async (courtRef) => {
    if (assignmentDetails[courtRef]) return; // Already fetched

    try {
      const response = await getBothCourtCasesDetailsByCourtRef(courtRef);
      const rawSource = response.data?.source || 'Unknown';
      const formattedSource = rawSource === 'highCourt' ? 'High Court' :
        rawSource === 'districtCourt' ? 'District Court' :
        rawSource === 'supremeCourt' ? 'Supreme Court' :
          rawSource;

      const details = {
        parties: response.data?.case?.parties || 'No parties information',
        source: formattedSource
      };
      setAssignmentDetails(prev => ({ ...prev, [courtRef]: details }));
    } catch (error) {
      console.error('Error fetching assignment details:', error);
    }
  };
  const getCurrentCases = () => {
    const cases = selectedCourtTab === 'high'
      ? highCourtCases
      : selectedCourtTab === 'district'
        ? districtCourtCases
        : selectedCourtTab === 'supreme'
          ? supremeCourtCases
          : otherCourtCases;

    // Ensure we always return an array
    return Array.isArray(cases) ? cases : [];
  };

  const getFilteredCases = () => {
    const currentCases = getCurrentCases();
    if (!searchTerm.trim()) return currentCases;

    return currentCases.filter(caseItem =>
      caseItem.parties?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.courtRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.caseDate?.caseTypeAndNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleCaseSelect = (courtRef) => {
    setNewCourtRef(courtRef);
    setSearchTerm(courtRef);
    setIsDropdownOpen(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setNewCourtRef(e.target.value);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleInputBlur = (e) => {
    // Don't close if clicking on dropdown content
    if (e.relatedTarget && dropdownRef.current?.contains(e.relatedTarget)) {
      return;
    }
    setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  const handleEditUser = () => {
    navigate(`/home/addusers/edit-user/${id}`, {
    state: { userData }
  });
  };

  const handleDeleteUser = () => {
    setShowUserDeleteModal(true);
  };

  const confirmUserDelete = async () => {
    setShowUserDeleteModal(false);
    showLoading("Deleting user...");
    try {
      await deleteAddedUser(id);
      showToast('User deleted successfully!', 'success');
      navigate('/home/addusers');
    } catch (error) {
      console.error('Failed to delete user:', error);
      showToast('Failed to delete user. Please try again.', 'error');
    } finally {
      hideLoading();
    }
  };

  const cancelUserDelete = () => {
    setShowUserDeleteModal(false);
  };

  const handleTabChange = (tab) => {
    setSelectedCourtTab(tab);
    setSearchTerm('');
    setNewCourtRef('');
    // Keep dropdown open when switching tabs
    setIsDropdownOpen(true);
  };
  // Outside click handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddCaseAssignment = async () => {
    if (!newCourtRef.trim()) return;

    setIsSubmitting(true);
    showLoading("Adding case assignment...");
    try {
      const payload = {
        courtRef: newCourtRef.trim(),
        user: id
      };

      await addCaseAssignmentToUser(payload);
      await fetchCaseAssignments(); // Refresh the list
      setNewCourtRef("");
      setSearchTerm("");
      setShowAddForm(false);
      showToast('Case assignment added successfully!', 'success');
      hideLoading();
    } catch (error) {
      console.error('Error adding case assignment:', error);
      showToast('Failed to add case assignment. Please try again.', 'error');
      hideLoading();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCaseAssignment = async (assignmentId) => {
    setDeleteModal({ isOpen: true, assignmentId });
  };

  const confirmDelete = async () => {
    const { assignmentId } = deleteModal;
    setDeletingId(assignmentId);
    setDeleteModal({ isOpen: false, assignmentId: null });

    showLoading("Deleting case assignment...");
    try {
      await deleteCaseAssignmentToUser(assignmentId);
      await fetchCaseAssignments(); // Refresh the list
      showToast('Case assignment deleted successfully!', 'success');
      hideLoading();
    } catch (error) {
      console.error('Error deleting case assignment:', error);
      showToast('Failed to delete case assignment. Please try again.', 'error');
      hideLoading();
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, assignmentId: null });
  };

  if (loading) {
    return null;
  }

  if (!userData) {
    return (
      <div className="user-detail-container">
        <div className="error-message">User not found</div>
      </div>
    );
  }

  return (
    <div className="user-detail-container">
      <div className="user-card">
        <div className="user-header">
          <div className="user-header-data">
            <div className="avatar">
              {userData.name.charAt(0).toUpperCase()}
            </div>
            <div className="user-title">
              <h2>{formatText(userData.name)}</h2>
              <span className={`user-status ${userData.isActive ? 'active' : 'inactive'}`}>
                {userData.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <div className="client-actions">
            <button 
              className="btn-update" 
              onClick={handleEditUser}
              disabled={userData.role === 'admin' || userData.role === 'superAdmin'}
              style={{ 
                cursor: (userData.role === 'admin' || userData.role === 'superAdmin') ? 'not-allowed' : 'pointer',
                opacity: (userData.role === 'admin' || userData.role === 'superAdmin') ? 0.5 : 1
              }}
            >
              <BiEdit /> Update
            </button>
            <button 
              className="btn-delete" 
              onClick={handleDeleteUser}
              disabled={userData.role === 'admin' || userData.role === 'superAdmin'}
              style={{ 
                cursor: (userData.role === 'admin' || userData.role === 'superAdmin') ? 'not-allowed' : 'pointer',
                opacity: (userData.role === 'admin' || userData.role === 'superAdmin') ? 0.5 : 1
              }}
            >
              <BiTrash /> Delete
            </button>
          </div>
        </div>
      

      <div className="user-details-grid">
        <div className="view-user-detailspage--detail-item">
          <FiMail className="icon" />
          <div>
            <label>Email</label>
            <p>{userData.email}</p>
          </div>
        </div>

        <div className="view-user-detailspage--detail-item">
          <FiPhone className="icon" />
          <div>
            <label>Phone</label>
            <p>{userData.phno || 'Not provided'}</p>
          </div>
        </div>

        <div className="view-user-detailspage--detail-item">
          <FiShield className="icon" />
          <div>
            <label>Role</label>
            <p className="role-badge">{userData.role}</p>
          </div>
        </div>

        <div className="view-user-detailspage--detail-item">
          <FiMapPin className="icon" />
          <div>
            <label>Branch</label>
            <p>{branchName || 'Not assigned'}</p>
          </div>
        </div>

        <div className="view-user-detailspage--detail-item">
          <FiUserPlus className="icon" />
          <div>
            <label>Created By</label>
            <p>{createdByName || 'System'}</p>
          </div>
        </div>

        <div className="view-user-detailspage--detail-item">
          <FiClock className="icon" />
          <div>
            <label>Created At</label>
            <p>{new Date(userData.createdAt).toLocaleString()}</p>
          </div>
        </div>

        <div className="view-user-detailspage--detail-item">
          <FiEdit className="icon" />
          <div>
            <label>Updated At</label>
            <p>{new Date(userData.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="case-assignments-section">
        <div className="section-header">
          <div className="section-title">
            <FiFileText className="icon" />
            <h3>Case Assignments</h3>
          </div>
          <button
            className="add-case-btn"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <FiPlus /> Add Case Assignment
          </button>
        </div>

        {/* Replace the existing add-case-form JSX with this: */}
        {showAddForm && (
          <div className="add-case-form">
            <div className="form-header">
              <h4>Add New Case Assignment</h4>
            </div>

            <div className="form-content">
              <div className="case-selector-dropdown" ref={dropdownRef}>
                <label>Select Court Reference</label>
                <div className="case-selector">
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="custom-dropdown-input"
                    placeholder="Search cases by court ref, parties, or case number..."
                    autoComplete="off"
                  />

                  {isDropdownOpen && (
                    <div className="dropdown-content">
                      <div className="dropdown-tabs">
                        <button
                          type="button"
                          className={`${selectedCourtTab === 'high' ? 'active' : ''}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleTabChange('high')}
                        >
                          High Court
                        </button>
                        <button
                          type="button"
                          className={`${selectedCourtTab === 'district' ? 'active' : ''}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleTabChange('district')}
                        >
                          District Court
                        </button>
                        <button
                          type="button"
                          className={`${selectedCourtTab === 'other' ? 'active' : ''}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleTabChange('other')}
                        >
                          Other Court
                        </button>
                        <button
                          type="button"
                          className={`${selectedCourtTab === 'supreme' ? 'active' : ''}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleTabChange('supreme')}
                        >
                          Supreme Court
                        </button>
                      </div>

                      <div className="dropdown-options">
                        {searchTerm && getFilteredCases().length === 0 && getCurrentCases().length > 0 && (
                          <div className="no-data">
                            No cases found matching "{searchTerm}"
                          </div>
                        )}

                        {getFilteredCases().length === 0 && !searchTerm ? (
                          <div className="no-data">
                            No {selectedCourtTab} court cases available
                          </div>
                        ) : (
                          getFilteredCases().map((caseItem, index) => (
                            <div
                              key={`${selectedCourtTab}-${index}`}
                              className="dropdown-option"
                              onClick={() => handleCaseSelect(caseItem.courtRef)}
                            >
                              <div className="case-info">
                                <div className="case-parties">
                                  {caseItem.parties || 'No parties information'}
                                </div>
                                <div className="case-ref">{caseItem.courtRef}</div>
                                {caseItem.caseDate?.caseTypeAndNumber && (
                                  <div className="case-number">
                                    {caseItem.caseDate.caseTypeAndNumber}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  className="save-btn"
                  onClick={handleAddCaseAssignment}
                  disabled={!newCourtRef.trim() || isSubmitting}
                >
                  <FiSave /> {isSubmitting ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewCourtRef("");
                    setSearchTerm("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Case Assignments List */}
        <div className="case-assignments-list">
          {loadingAssignments ? (
            <div className="loading-state">Loading case assignments...</div>
          ) : caseAssignments.length === 0 ? (
            <div className="empty-state">
              No case assignments found for this user.
            </div>
          ) : (
            <div className="assignments-grid">
              {caseAssignments.map((assignment) => (
                <div key={assignment._id} className="assignment-card">
                  <div className="assignment-info">
                    <div className="court-ref" style={{ cursor: 'pointer' }} onClick={() => navigate(`/home/cases/casedetails/${assignment.courtRef}`)}>{assignment.courtRef}</div>
                    <div className="parties-info" style={{ cursor: 'pointer' }} onClick={() => navigate(`/home/cases/casedetails/${assignment.courtRef}`)}>
                      {formatPartyNames(assignmentDetails[assignment.courtRef]?.parties) || 'Loading parties...'}
                    </div>
                    <div className="source-info">
                      Source: {assignmentDetails[assignment.courtRef]?.source || 'Loading...'}
                    </div>
                    <div className="assignment-meta">
                      <span className="created-date">
                        Created: {new Date(assignment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteCaseAssignment(assignment._id)}
                    disabled={deletingId === assignment._id}
                  >
                    {deletingId === assignment._id ? (
                      <div className="mini-spinner"></div>
                    ) : (
                      <FiTrash2 />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType="this case assignment"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      <DeleteConfirmationModal isOpen={showUserDeleteModal} itemType={`user "${userData.name}"`} onConfirm={confirmUserDelete} onCancel={cancelUserDelete} />
    </div>      
    </div>
  );
};

export default UserDetailPage;